/**
 * Workflow-service schedule endpoints. Injected into the shared `integrationApi`.
 *
 *   POST   /workflow/v1/schedules              → { scheduleId, workflowName, cron, taskQueue }
 *   DELETE /workflow/v1/schedules/{scheduleId} → { scheduleId, deleted }   (absent id → deleted:false, no error)
 *
 * A polling-only storefront plugin (no webhook) declares the schedules it needs in its
 * `plugin-installed` snippet result. The portal, which holds the signed-in admin's token,
 * creates them here and stores the ids on the merchant plugin. Snippets never call
 * OrderMesh services themselves.
 */
import { services } from '@config/services';
import { integrationApi } from './_base';

/** One schedule as declared by a `plugin-installed` snippet. */
export interface ScheduleDeclaration {
  /** Temporal workflow type name started on each tick. */
  workflowName: string;
  /** Repeating cadence (5 or 6 fields). Exactly one of `cron` or `runOnceAt`. */
  cron?: string;
  /** One-shot instant (ISO 8601, UTC). Exactly one of `cron` or `runOnceAt`. */
  runOnceAt?: string;
  /** Stop a `cron` schedule after this instant (ISO 8601, UTC). */
  endAt?: string;
  /** Task queue the worker listens on. */
  taskQueue: string;
  /** Workflow input, passed as the single argument on each tick. */
  args?: Record<string, string>;
  /**
   * Plugin variable that receives the created schedule id, for example
   * `PollingScheduleId`. The uninstall path reads it back to delete the schedule.
   */
  idVariable: string;
}

export interface CreateScheduleBody {
  merchantPluginId: string;
  workflowName: string;
  /** Plugin integrator, for example "AmazonDF". Readable part of the schedule id. */
  integratorId?: string;
  /** Merchant that owns the install. Readable part of the schedule id. */
  merchantId?: string;
  cron?: string;
  runOnceAt?: string;
  endAt?: string;
  taskQueue: string;
  args: Record<string, string>;
}

export interface CreateScheduleResponse {
  scheduleId: string;
  workflowName?: string;
  cron?: string | null;
  runOnceAt?: string | null;
  endAt?: string | null;
  taskQueue?: string;
  [key: string]: unknown;
}

export interface DeleteScheduleResponse {
  scheduleId: string;
  deleted: boolean;
  [key: string]: unknown;
}

/** Exported request builders — wire-shape lock tests assert these directly. */
export const scheduleRequestBuilders = {
  // POST /workflow/v1/schedules
  createSchedule: (body: CreateScheduleBody) => ({
    url: `${services.workflow}/v1/schedules`,
    method: 'POST' as const,
    body,
  }),
  // DELETE /workflow/v1/schedules/{scheduleId}
  deleteSchedule: (scheduleId: string) => ({
    url: `${services.workflow}/v1/schedules/${encodeURIComponent(scheduleId)}`,
    method: 'DELETE' as const,
  }),
};

/**
 * Returns the trimmed string, or undefined when the value is not a non-blank string.
 */
function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Workflow args must be scalars. A nested object would reach Temporal as
 * "[object Object]", so a declaration with one is rejected as a whole.
 */
function parseScheduleArgs(value: unknown): Record<string, string> | undefined | null {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'object' || Array.isArray(value)) return null;
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
      out[key] = String(raw);
    } else {
      return null;
    }
  }
  return out;
}

/**
 * Parses one `schedulesToCreate` entry. Returns null when the entry is unusable.
 */
function parseScheduleDeclaration(item: unknown): ScheduleDeclaration | null {
  if (!item || typeof item !== 'object') return null;
  const s = item as Record<string, unknown>;
  const workflowName = optionalString(s.workflowName);
  const taskQueue = optionalString(s.taskQueue);
  const idVariable = optionalString(s.idVariable);
  if (!workflowName || !taskQueue || !idVariable) return null;

  const cron = optionalString(s.cron);
  const runOnceAt = optionalString(s.runOnceAt);
  if (Boolean(cron) === Boolean(runOnceAt)) return null;

  const args = parseScheduleArgs(s.args);
  if (args === null) return null;

  return { workflowName, taskQueue, idVariable, cron, runOnceAt, endAt: optionalString(s.endAt), args };
}

/**
 * Reads the `schedulesToCreate` array off a `plugin-installed` result. Entries without the
 * required fields, with both or neither of `cron` / `runOnceAt`, or with non-scalar args are
 * dropped, so one malformed declaration cannot create a broken schedule.
 */
export function parseScheduleDeclarations(value: unknown): ScheduleDeclaration[] {
  if (!Array.isArray(value)) return [];
  return value.map(parseScheduleDeclaration).filter((d): d is ScheduleDeclaration => d !== null);
}

/**
 * Reads the `schedulesToDelete` array off a `plugin-uninstalled` result: the names of the
 * plugin variables that hold schedule ids (for example `PollingScheduleId`). Non-strings
 * and blanks are dropped.
 */
export function parseScheduleIdVariables(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const name = item.trim();
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

const schedulesApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /** POST /workflow/v1/schedules — create one Temporal schedule for a merchant plugin. */
    createSchedule: builder.mutation<CreateScheduleResponse, CreateScheduleBody>({
      query: (body) => scheduleRequestBuilders.createSchedule(body),
    }),
    /** DELETE /workflow/v1/schedules/{scheduleId} — deleting an absent schedule is a no-op. */
    deleteSchedule: builder.mutation<DeleteScheduleResponse, string>({
      query: (scheduleId) => scheduleRequestBuilders.deleteSchedule(scheduleId),
    }),
  }),
});

export const { useCreateScheduleMutation, useDeleteScheduleMutation } = schedulesApi;
